import { type Event, EventListener, KeyCodes } from "silver";
import { generateId } from "core/Misc";
import { toggleChildNodeAvailability } from "shared/functions";

export interface Tab<TabId extends string = string>{
	tabId: TabId;
	tab: HTMLElement;
	tabpanel: HTMLElement;
}

interface TabEvent<TabId extends string = string> extends Event {
	sender: any;
	tab: Readonly<Tab<TabId>>;
}

export class Tabset<TabId extends string = string> {

	public static readonly onTabActivate = EventListener.create<TabEvent>("tabActivate");
	public static readonly onTabDeactivate = EventListener.create<TabEvent>("tabDeactivate");

	public readonly onTabActivate = EventListener.create<TabEvent<TabId>>("tabActivate");
	public readonly onTabDeactivate = EventListener.create<TabEvent<TabId>>("tabDeactivate");

	private readonly tabs: Record<string, Tab<TabId>> = { };
	private readonly tabOrder: TabId[] = [];

	public urlParamValue?: string;
	private activeTabName?: TabId;

	constructor(
		public readonly element: HTMLElement,
		protected tabList: HTMLElement,
		private panelContainer: HTMLElement
	) {
		if (!element.id) {
			element.id = generateId("tabset-");
		}
		this.tabList.setAttribute("role", "tablist");
	}

	public add(tab: Tab<TabId>): void {
		if (this.tabOrder.indexOf(tab.tabId) !== -1) {
			throw new Error("Tab alread registered");
		}
		if (!tab.tab.id) {
			tab.tab.id = generateId("tabthumb-");
		}
		if (!tab.tabpanel.id) {
			tab.tabpanel.id = generateId("tabpanel-");
		}

		tab.tab.setAttribute("role", "tab");
		tab.tab.setAttribute("aria-controls", tab.tabpanel.id);
		tab.tabpanel.setAttribute("role", "tabpanel");
		tab.tabpanel.setAttribute("aria-labelledby", tab.tab.id);

		this.tabOrder.push(tab.tabId);
		this.tabList.appendChild(tab.tab);
		this.panelContainer.appendChild(tab.tabpanel);

		this.tabs[ tab.tabId ] = tab;

		tab.tab.addEventListener("keyup", (event: KeyboardEvent) => {
			this.handleTabKeyup(tab, event);
		});

		tab.tab.addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this.showTab(tab.tabId, true);
		});

		if (this.tabOrder.length === 1) {
			this.showTab(tab.tabId, false);
		} else {
			this.handleTabStateChange(tab, false);
		}
	}

	protected handleTabStateChange(tab: Tab<TabId>, active: boolean): void {
		tab.tab.setAttribute("tabindex", active ? "0" : "-1");
		tab.tab.setAttribute("aria-selected", active ? "true" : "false");
		tab.tabpanel.setAttribute("tabindex", active ? "0" : "-1");
		tab.tabpanel.setAttribute("aria-hidden", active ? "false" : "true");
		(active ? this.onTabActivate : this.onTabDeactivate).trigger({ tab, sender: this });
		(active ? Tabset.onTabActivate : Tabset.onTabDeactivate).trigger({ tab, sender: this });
		toggleChildNodeAvailability(tab.tabpanel, active);

		this.updateTabUrl();
	}

	public get urlParam(): string|undefined {
		return this.urlParamValue;
	}

	public set urlParam(value: string|undefined) {
		this.urlParamValue = value;
		this.updateTabUrl();
	}

	private updateTabUrl(): void {
		if (!this.urlParam || !this.activeTab) {
			return;
		}
		const urlParams = new URLSearchParams(location.search);
		const currentTab = urlParams.get(this.urlParam);
		if (currentTab != this.activeTab.tabId) {
			urlParams.set(this.urlParam, this.activeTab.tabId);
			history.replaceState({}, "", location.pathname + "?" + urlParams.toString());
		}
	}

	public showTab(tabId: TabId, focus: boolean = false): void {
		const tab = this.tabs[tabId];
		if (tabId !== this.activeTabName) {
			if (this.activeTabName) {
				this.handleTabStateChange(this.tabs[this.activeTabName], false);
			}
			this.activeTabName = tabId;
			this.handleTabStateChange(tab, true);
		}
		if (focus) {
			tab.tab.focus();
		}
	}

	public get activeTab(): undefined|Tab<TabId> {
		if (this.activeTabName && this.tabs.hasOwnProperty(this.activeTabName)) {
			return this.tabs[this.activeTabName];
		}
		return undefined;
	}

	public forEachTab(callback: (tab: Readonly<Tab<TabId>>) => void): void {
		for (const tabId of this.tabOrder) {
			callback(this.tabs[tabId]);
		}
	}

	private handleTabKeyup(tab: Tab<TabId>, event: KeyboardEvent): void {
		const index = this.tabOrder.indexOf(tab.tabId);
		let nextIndex: number|undefined;
		const keyCode = event.keyCode;

		if (index === -1) {
			return;
		}
		if (keyCode == KeyCodes.HOME) {
			nextIndex = 0;
		} else if (keyCode == KeyCodes.END) {
			nextIndex = this.tabOrder.length - 1;
		} else if (keyCode == KeyCodes.LEFT_ARROW || keyCode == KeyCodes.UP_ARROW) {
			nextIndex = (index - 1);
		} else if (keyCode == KeyCodes.RIGHT_ARROW || keyCode == KeyCodes.DOWN_ARROW) {
			nextIndex = (index + 1);
		} else {
			return;
		}

		event.stopPropagation();
		event.preventDefault();

		nextIndex = (nextIndex + this.tabOrder.length) % this.tabOrder.length;
		this.showTab(this.tabOrder[ nextIndex ], true);
	}
}

